// ScanAxisEditors — the per-axis inline editor bottom sheet and its
// inner editor views (canonical / brand / category / size / location /
// tile / state / cut). Extracted verbatim from ScanPipeline.jsx
// (pure move).

import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useIngredientInfo } from "../../../lib/useIngredientInfo";
import {
  allCanonicals, searchCanonicals,
  statesForIngredient, STATE_LABELS,
  cutsForIngredient, cutLabel,
} from "../../../data/ingredients";
import { FRIDGE_TILES } from "../../../lib/fridgeTiles";
import { PANTRY_TILES } from "../../../lib/pantryTiles";
import { FREEZER_TILES } from "../../../lib/freezerTiles";
import { tilesForIngredient } from "../../../lib/tileMatching";
import { usePopularPackages } from "../../../lib/usePopularPackages";
import { brandsForCanonical, BRAND_EXPERTISE, brandSlugify } from "../../../data/brandExpertise";
import { useBrandObservations } from "../../../lib/useBrandObservations";
import { useBrandsFromPantry } from "../../../lib/useBrandsFromPantry";
import { useBrandIdentity } from "../../../lib/useBrandIdentity";
import { useShoppingRegion } from "../../../lib/useShoppingRegion";
import { FONT, COLOR, CHIP_TONES } from "../../../lib/tokens";
import {
  SIZE_UNIT_OPTIONS, CATEGORY_OPTIONS,
  fallbackSizesFor, mergeSuggestedAndFallback,
  primaryActionStyle, ghostActionStyle,
} from "./scanShared";

// ── Inline editor — bottom sheet picker per axis ────────────────────
// One generic sheet for all four supported axes. Picks the right
// inner view based on `editor.axis`. The `--kb-h` CSS variable is
// kept in sync globally by App.jsx (Capacitor's keyboardWillShow /
// keyboardWillHide → `--kb-h`); the backdrop's paddingBottom reads
// from it so the sheet sits above the keyboard without per-mount
// listener wiring.

export function InlineEditor({ editor, draft, store, onClose }) {
  const axis = editor.axis;
  return createPortal(
    <div style={editorBackdropStyle} onClick={onClose}>
      <div
        style={editorSheetStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={editorHeaderStyle}>
          <span style={{ fontFamily: FONT.forma, fontSize: 10, fontWeight: 700, color: COLOR.muted, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            EDIT · {axis}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="mcm-focusable"
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
              color: COLOR.muted, letterSpacing: "0.10em", textTransform: "uppercase",
              padding: "4px 6px",
            }}
          >
            Done
          </button>
        </div>
        <div style={editorBodyStyle}>
          {axis === "canonical" && (
            <CanonicalEditor
              draft={draft}
              onPick={(ing) => {
                store.setCanonical(ing.id, ing.name, ing.emoji || null, "user");
                onClose();
              }}
            />
          )}
          {axis === "brand" && (
            <BrandEditor
              draft={draft}
              onSet={(value) => { store.setAxis("brand", value || null, "user"); onClose(); }}
            />
          )}
          {axis === "category" && (
            <CategoryEditor
              draft={draft}
              onPick={(opt) => { store.setAxis("category", opt.id, "user"); onClose(); }}
            />
          )}
          {axis === "size" && (
            <SizeEditor
              draft={draft}
              onSet={(amount, unit) => {
                store.setAxis("packageAmount", amount, "user");
                store.setAxis("packageUnit",   unit,   "user");
                onClose();
              }}
            />
          )}
          {axis === "location" && (
            <SimpleListEditor
              options={LOCATION_INLINE_OPTIONS}
              value={draft.location}
              onPick={(opt) => { store.setAxis("location", opt.id, "user"); onClose(); }}
            />
          )}
          {axis === "tile" && (
            <TileInlineEditor
              draft={draft}
              onPick={(opt) => { store.setAxis("tileId", opt.id, "user"); onClose(); }}
            />
          )}
          {axis === "state" && (
            <SimpleListEditor
              options={(statesForIngredient(draft.canonicalId) || [])
                .map((s) => typeof s === "string" ? { id: s, label: STATE_LABELS?.[s] || s } : { id: s.id, label: s.label || s.id })}
              value={draft.state}
              empty="No states defined for this canonical."
              onPick={(opt) => { store.setAxis("state", opt.id, "user"); onClose(); }}
            />
          )}
          {axis === "cut" && (
            <SimpleListEditor
              options={(cutsForIngredient(draft.canonicalId) || [])
                .map((c) => typeof c === "string" ? { id: c, label: cutLabel?.(c) || c } : { id: c.id, label: c.label || c.id })}
              value={draft.cut}
              empty="No cuts defined for this canonical."
              onPick={(opt) => { store.setAxis("cut", opt.id, "user"); onClose(); }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export const LOCATION_INLINE_OPTIONS = [
  { id: "fridge",  label: "Fridge" },
  { id: "pantry",  label: "Pantry" },
  { id: "freezer", label: "Freezer" },
];

export function SimpleListEditor({ options, value, onPick, empty }) {
  if (!options || options.length === 0) {
    return (
      <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted, padding: "12px 4px" }}>
        {empty || "No options."}
      </div>
    );
  }
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <li key={opt.id}>
            <button
              type="button"
              onClick={() => onPick(opt)}
              className="mcm-focusable"
              style={{
                width: "100%",
                padding: "10px 12px",
                textAlign: "left",
                fontFamily: FONT.forma,
                fontStyle: "italic",
                fontSize: 15,
                color: COLOR.ink,
                background: active ? "rgba(47,143,131,0.10)" : "rgba(255,255,255,0.04)",
                border: `1px solid ${active ? COLOR.leaf : COLOR.edge}`,
                borderRadius: 12,
                cursor: "pointer",
              }}
            >
              {opt.label}
              {active && <span style={{ float: "right", color: COLOR.leaf }}>✓</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function TileInlineEditor({ draft, onPick }) {
  // Tile pool by location, with the canonical's recommended tile up
  // top — same shape ResolveCard uses.
  const loc = draft.location;
  const pool =
    loc === "fridge"  ? FRIDGE_TILES :
    loc === "freezer" ? FREEZER_TILES :
    loc === "pantry"  ? PANTRY_TILES :
    [...PANTRY_TILES, ...FRIDGE_TILES, ...FREEZER_TILES];
  const recommended = new Set(tilesForIngredient(draft.canonicalId));
  const sorted = [...pool].sort((a, b) =>
    (recommended.has(a.id) ? 0 : 1) - (recommended.has(b.id) ? 0 : 1)
  );
  const options = sorted.map((t) => ({
    id: t.id,
    label: t.label || t.id,
  }));
  return (
    <SimpleListEditor
      options={options}
      value={draft.tileId}
      onPick={onPick}
    />
  );
}

export function CanonicalEditor({ draft, onPick }) {
  const [q, setQ] = useState("");
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    // Both tiers (bundled + synthetic) via the sanctioned entry
    // points — the bundled-only filter hid user-minted canonicals
    // from this picker.
    if (!needle) return allCanonicals().slice(0, 18);
    return searchCanonicals(needle, 30);
  }, [q]);
  return (
    <>
      <input
        autoFocus
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search canonicals…"
        style={editorInputStyle}
      />
      <div style={editorListStyle}>
        {results.map((ing) => (
          <button
            key={ing.id}
            type="button"
            className="mcm-focusable"
            onClick={() => onPick(ing)}
            style={{
              ...editorListItemStyle,
              background: ing.id === draft.canonicalId
                ? CHIP_TONES.canonical.bg : "transparent",
            }}
          >
            {ing.emoji && <span style={{ fontSize: 18, marginRight: 8 }}>{ing.emoji}</span>}
            <span style={{ flex: 1, textAlign: "left" }}>{ing.name}</span>
            {ing.id === draft.canonicalId && <span style={{ color: CHIP_TONES.canonical.fg }}>✓</span>}
          </button>
        ))}
        {results.length === 0 && (
          <div style={{ padding: "12px 4px", fontFamily: FONT.forma, fontSize: 12, color: COLOR.muted }}>
            No matches.
          </div>
        )}
      </div>
    </>
  );
}

export function BrandEditor({ draft, onSet }) {
  const [val, setVal] = useState(draft.brand || "");
  const canonicalId = draft.canonicalId || null;

  // Same tiered resolver the wizard's BrandStep uses. Closes the
  // "scan a UPC, brand chip opens a bare input with no suggestions"
  // gap. Per memory feedback_kitchen_inflow_pipelines.md: every
  // identity-axis editor across pipelines must surface the same
  // brand catalog.
  const { getInfo } = useIngredientInfo();
  const { observations: rawObservations } = useBrandObservations(canonicalId);
  const pantryBrands = useBrandsFromPantry(canonicalId);
  const observations = useMemo(() => {
    const out = [...(Array.isArray(rawObservations) ? rawObservations : [])];
    const seen = new Set(out.map((o) => (o.slug || (o.brand || "").toLowerCase().trim())));
    for (const p of pantryBrands || []) {
      const key = (p.brand || "").toLowerCase().trim();
      if (!key || seen.has(key)) continue;
      out.push(p);
      seen.add(key);
    }
    return out;
  }, [rawObservations, pantryBrands]);
  const { bySlug: brandIdentities } = useBrandIdentity();
  const [region] = useShoppingRegion();

  const suggestions = useMemo(() => {
    if (!canonicalId) return [];
    const info = getInfo?.(canonicalId) || null;
    return brandsForCanonical(canonicalId, {
      limit: 16, observations, info, region, brandIdentities,
    });
  }, [canonicalId, observations, getInfo, region, brandIdentities]);

  const specialtySlugs = useMemo(() => {
    const set = new Set();
    for (const obs of observations || []) {
      const display = obs?.display || obs?.brand || null;
      const slug = obs?.slug || (display ? brandSlugify(display) : null);
      if (slug) set.add(slug);
    }
    if (canonicalId) {
      for (const [slug, rec] of Object.entries(BRAND_EXPERTISE)) {
        if (Number(rec?.canonicals?.[canonicalId] || 0) > 0) set.add(slug);
      }
    }
    return set;
  }, [observations, canonicalId]);

  const trimmedLower = val.trim().toLowerCase();
  const visible = useMemo(() => {
    if (!trimmedLower) return suggestions;
    return suggestions.filter((s) =>
      String(s.display || "").toLowerCase().includes(trimmedLower),
    );
  }, [suggestions, trimmedLower]);
  const specialty = visible.filter((s) => specialtySlugs.has(s.slug));
  const common = visible.filter((s) => !specialtySlugs.has(s.slug));

  const renderPill = (s) => (
    <button
      key={s.slug}
      type="button"
      className="mcm-focusable"
      onClick={() => onSet(s.display)}
      style={{
        display: "inline-flex", alignItems: "center",
        padding: "6px 12px",
        fontFamily: FONT.itemTitle, fontStyle: "italic",
        fontSize: 14, fontWeight: 500,
        color: COLOR.ink,
        background: COLOR.ground,
        border: `1px solid ${COLOR.edge}`,
        borderRadius: 999,
        cursor: "pointer",
      }}
    >
      {s.display}
    </button>
  );

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSet(val.trim()); }}
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      <input
        autoFocus
        type="text"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Brand…"
        style={editorInputStyle}
      />

      {specialty.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{
            fontFamily: FONT.mono, fontSize: 11,
            letterSpacing: "0.16em", textTransform: "uppercase",
            color: COLOR.ink,
          }}>
            Curated for this item
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {specialty.map(renderPill)}
          </div>
        </div>
      )}

      {common.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{
            fontFamily: FONT.mono, fontSize: 11,
            letterSpacing: "0.16em", textTransform: "uppercase",
            color: COLOR.muted,
          }}>
            {region ? "Common local brands" : "Common brands"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {common.map(renderPill)}
          </div>
        </div>
      )}

      <button type="submit" className="mcm-focusable" style={primaryActionStyle(true)}>
        SAVE BRAND
      </button>
      {draft.brand && (
        <button
          type="button"
          onClick={() => onSet("")}
          className="mcm-focusable"
          style={ghostActionStyle}
        >
          Clear brand
        </button>
      )}
    </form>
  );
}

export function CategoryEditor({ draft, onPick }) {
  return (
    <div style={editorListStyle}>
      {CATEGORY_OPTIONS.map((opt) => (
        <button
          key={opt.id}
          type="button"
          className="mcm-focusable"
          onClick={() => onPick(opt)}
          style={{
            ...editorListItemStyle,
            background: opt.id === draft.category ? CHIP_TONES.category.bg : "transparent",
          }}
        >
          <span style={{ flex: 1, textAlign: "left" }}>{opt.label}</span>
          {opt.id === draft.category && <span style={{ color: CHIP_TONES.category.fg }}>✓</span>}
        </button>
      ))}
    </div>
  );
}

export function SizeEditor({ draft, onSet }) {
  const [amount, setAmount] = useState(draft.packageAmount != null ? String(draft.packageAmount) : "");
  const [unit, setUnit]     = useState(draft.packageUnit || "oz");
  const valid = amount && Number(amount) > 0 && unit;
  // Suggested sizes — observed (popular_package_sizes + AI typical
  // sizes) merged with category-keyed fallbacks so the picker is
  // never empty. User wanted "just dump the common sizes and let me
  // pick" — this guarantees that.
  const { rows: observed } = usePopularPackages(draft.brand || null, draft.canonicalId || null, 6);
  const fallback = useMemo(() => fallbackSizesFor(draft.canonicalId), [draft.canonicalId]);
  const suggested = useMemo(
    () => mergeSuggestedAndFallback(observed, fallback, 10),
    [observed, fallback],
  );
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (valid) onSet(Number(amount), unit); }}
      style={{ display: "flex", flexDirection: "column", gap: 10 }}
    >
      {suggested.length > 0 && (
        <>
          <div style={{
            fontFamily: FONT.forma, fontSize: 9, fontWeight: 700,
            color: COLOR.muted, letterSpacing: "0.16em", textTransform: "uppercase",
          }}>
            Tap a common size
            {draft.brand ? ` · ${draft.brand}` : ""}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {suggested.map((s, i) => {
              const isActive =
                Number(s.amount) === Number(amount) && s.unit === unit;
              return (
                <button
                  key={`${s.amount}|${s.unit}|${i}`}
                  type="button"
                  onClick={() => onSet(Number(s.amount), s.unit)}
                  className="mcm-focusable"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "8px 14px",
                    borderRadius: 999,
                    // Neutral by default; green only when this is the
                    // paired / picked size. No blue / tan / red on
                    // unpaired pills.
                    border: isActive
                      ? `1px solid ${COLOR.leaf}`
                      : `1px solid ${COLOR.edge}`,
                    background: isActive
                      ? "rgba(47,143,131,0.14)"
                      : "transparent",
                    color: isActive ? COLOR.leaf : COLOR.ink,
                    fontFamily: FONT.forma, fontSize: 13, fontWeight: 600,
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  <span>{s.amount} {s.unit}</span>
                </button>
              );
            })}
          </div>
          <div style={{
            fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
            color: COLOR.dim, letterSpacing: "0.10em", textTransform: "uppercase",
            marginTop: 4,
          }}>
            Or type a custom size
          </div>
        </>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <input
          autoFocus={suggested.length === 0}
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
          placeholder="Amount"
          style={{ ...editorInputStyle, flex: 1 }}
        />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {SIZE_UNIT_OPTIONS.map((u) => {
          const active = u === unit;
          return (
            <button
              key={u}
              type="button"
              className="mcm-focusable"
              onClick={() => setUnit(u)}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                border: `1px solid ${active ? COLOR.leaf : COLOR.edge}`,
                background: active ? "rgba(47,143,131,0.14)" : "transparent",
                color: active ? COLOR.leaf : COLOR.ink,
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              {u}
            </button>
          );
        })}
      </div>
      <button
        type="submit"
        disabled={!valid}
        className="mcm-focusable"
        style={primaryActionStyle(!!valid)}
      >
        SAVE SIZE
      </button>
    </form>
  );
}

const editorBackdropStyle = {
  position: "fixed", inset: 0,
  zIndex: 1100,
  background: "rgba(0,0,0,0.45)",
  display: "flex", alignItems: "flex-end", justifyContent: "center",
  // Capacitor `Keyboard.resize: 'body'` doesn't move position:fixed
  // elements out of the keyboard's way. The InlineEditor effect above
  // mirrors the keyboard height into --kb-h while it's mounted; pad
  // the backdrop's bottom so the flex-end sheet sits ABOVE the
  // keyboard instead of behind it. Falls back to 0 on web / when the
  // listeners haven't fired yet.
  paddingBottom: "var(--kb-h, 0px)",
  transition: "padding-bottom 200ms ease",
};

const editorSheetStyle = {
  width: "100%",
  maxWidth: 480,
  // Cap height so a long list (e.g. tile picker) doesn't push the
  // header + Done button off-screen. dvh tracks the visible viewport
  // on iOS even when the URL bar shows.
  maxHeight: "min(85dvh, 720px)",
  background: COLOR.night,
  borderTopLeftRadius: 22,
  borderTopRightRadius: 22,
  borderTop: `1px solid ${COLOR.border}`,
  borderLeft: `1px solid ${COLOR.border}`,
  borderRight: `1px solid ${COLOR.border}`,
  boxShadow: "0 -18px 40px rgba(0,0,0,0.25)",
  display: "flex", flexDirection: "column",
  overflow: "hidden",
};

const editorHeaderStyle = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "12px 16px 8px",
  borderBottom: `1px solid ${COLOR.edge}`,
  flex: "0 0 auto",
};

const editorBodyStyle = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: "12px 14px calc(14px + env(safe-area-inset-bottom, 0px))",
  display: "flex", flexDirection: "column", gap: 10,
};

const editorInputStyle = {
  width: "100%", padding: "12px 14px",
  fontFamily: FONT.forma, fontSize: 16, fontWeight: 500,
  color: COLOR.ink,
  background: COLOR.deep,
  border: `1px solid ${COLOR.border}`,
  borderRadius: 10, outline: "none",
  boxSizing: "border-box",
  WebkitAppearance: "none",
};

const editorListStyle = {
  display: "flex", flexDirection: "column", gap: 2,
  maxHeight: 320, overflowY: "auto",
  WebkitOverflowScrolling: "touch",
};

const editorListItemStyle = {
  display: "flex", alignItems: "center",
  width: "100%", padding: "10px 10px",
  borderRadius: 8,
  border: "1px solid transparent",
  cursor: "pointer",
  fontFamily: FONT.forma, fontSize: 14, fontWeight: 500,
  color: COLOR.ink,
  textAlign: "left",
  touchAction: "manipulation",
  background: "transparent",
};
